"use client";

import type { AgentThreadShareVo } from "@/api";
import {
  CopyIcon,
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
} from "@/assets/icons";
import CopiedSuccessIcon from "@/assets/icons/copied-success.svg";
import RemixIcon from "@/assets/icons/remix.svg";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import Button from "@/component/Button";
import { ChatBoxFiles } from "@/component/ChatBoxFiles";
import { useShareLink } from "@/hooks/useShareLink";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { getThreadSharePath } from "@/util/share";
import "@ant-design/v5-patch-for-react-19";
import classNames from "classnames";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import SimpleBar from "simplebar-react";
import { MAX_UPLOAD_FILE_COUNT } from "../../../common/const";
import OpenIcon from "../assets/open-icon.svg";

interface ThreadSidebarProps {
  thread: AgentThreadShareVo;
  onRemix: () => void;
  onOpenProject: () => void;
  mediaHeight?: number;
}

interface SocialIconButtonProps {
  className: string;
  onClick: () => void;
  icon: ReactNode;
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div className="text-sm text-text-secondary font-bold">{children}</div>
);

const ThreadSection = ({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <section className={classNames("mt-5", className)}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </section>
);

const SocialIconButton = ({
  onClick,
  icon,
  className,
}: SocialIconButtonProps) => (
  <Button type="default" className={className} onClick={onClick}>
    {icon}
  </Button>
);

export function ThreadSidebar({
  thread,
  onRemix,
  onOpenProject,
  mediaHeight,
}: ThreadSidebarProps) {
  const shareLink = thread.id ? getThreadSharePath(thread.id) : "";
  const [isCopied, setIsCopied] = useState(false);

  const { shareOnTwitter, shareOnFacebook, shareOnLinkedin, handleCopyLink } =
    useShareLink({
      shareLink,
    });

  const shareActions = useMemo(
    () => [
      {
        key: "twitter",
        onClick: () => {
          shareOnTwitter();
          logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
            thread_id: thread.id,
            type: "twitter",
          });
        },
        icon: <TwitterIcon className="w-4 h-4" />,
      },
      {
        key: "facebook",
        onClick: () => {
          shareOnFacebook();
          logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
            thread_id: thread.id,
            type: "facebook",
          });
        },
        icon: <FacebookIcon className="w-4 h-4" />,
      },
      {
        key: "linkedin",
        onClick: () => {
          shareOnLinkedin();
          logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
            thread_id: thread.id,
            type: "linkedin",
          });
        },
        icon: <LinkedinIcon className="w-4 h-4" />,
      },
    ],
    [shareOnFacebook, shareOnLinkedin, shareOnTwitter, thread.id]
  );

  const onCopyLinkClick = () => {
    const success = handleCopyLink();
    if (success) {
      setIsCopied(true);
      logger.event(VidMuseEvent.ThreadSharePageCopyLinkBtnClick, {
        thread_id: thread.id,
      });
    }
  };

  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => setIsCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [isCopied]);

  return (
    <aside className="flex w-full pt-0 lg:w-[340px] max-h-[500px]  lg:max-h-full lg:items-center">
      <div
        className="w-full flex flex-col"
        style={{
          height: mediaHeight ? `${Math.max(mediaHeight, 500)}px` : "500px",
        }}
      >
        <h2 className="text-text-primary text-xl leading-normal font-bold">
          {thread.title || "Untitled"}
        </h2>

        <ThreadSection title="Prompt" className="flex flex-col flex-1 min-h-0 ">
          <div className="flex flex-col flex-1 mt-[10px] p-4 bg-background-card rounded-xl min-h-0">
            {thread.files && thread.files.length > 0 && (
              <div className="pb-3">
                <ChatBoxFiles
                  attachmentList={thread.files
                    .slice(0, MAX_UPLOAD_FILE_COUNT)
                    .map((file, index) => ({
                      type: "url",
                      id: `file-${index}`,
                      url: file.filePath
                        ? convertFilePathToUrl(file.filePath)
                        : "",
                      preview: file.filePath
                        ? convertFilePathToUrl(file.filePath, {
                            level: "medium",
                          })
                        : "",
                    }))}
                  size="small"
                  disabled
                />
              </div>
            )}

            <div className="flex-1 min-h-0">
              <SimpleBar className="w-full h-full text-sm text-text-secondary break-words">
                {thread.prompt || ""}
              </SimpleBar>
            </div>
          </div>
        </ThreadSection>

        <ThreadSection title="Share">
          <div className="mt-[10px] flex items-center gap-3 text-text-primary/80">
            {shareActions.map(({ key, onClick, icon }) => (
              <SocialIconButton
                className="flex-1 h-9! bg-background-card"
                key={key}
                onClick={onClick}
                icon={icon}
              />
            ))}

            {isCopied ? (
              <div className="flex-3 gap-1 h-9 text-sm flex items-center justify-center text-text-primary/80 bg-background-card rounded-full font-medium min-w-[130px]">
                Link copied <CopiedSuccessIcon className="w-4 h-4" />
              </div>
            ) : (
              <Button
                type="default"
                onClick={onCopyLinkClick}
                className="flex-3 gap-2 min-w-[130px]! h-9! bg-background-card"
                icon={<CopyIcon className="w-4 h-4" />}
              >
                Copy Link
              </Button>
            )}
          </div>
        </ThreadSection>

        <div className="mt-5 flex items-center gap-4">
          <Button
            type="primary"
            onClick={onRemix}
            className="flex-1 h-10!"
            icon={<RemixIcon className="w-4 h-4" />}
          >
            Remix
          </Button>
          <Button
            type="default"
            onClick={onOpenProject}
            className="flex-1 h-10! text-text-primary bg-background-card"
            icon={<OpenIcon className="w-4 h-4" />}
          >
            Open project
          </Button>
        </div>
      </div>
    </aside>
  );
}
